/*
  Tuotesivu: galleria, variaatiovalitsin ja mobiilin kiinteä ostopalkki.
  Hinnat ja saatavuus päivitetään Shopifyn Section Rendering API:lla (?variant=…&section_id=…),
  jotta rahamuotoilu ja saatavuus tulevat aina Shopifysta.
*/
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  // Kaupan rahamuoto (shop.money_format) – käytetään vain, jos Shopifyn renderöimää hintaa ei saada
  const formatMoney = (cents, fmt) => {
    const v = Number(cents) / 100;
    const n = (dec, thou, dp = 2) => {
      const [i, f] = v.toFixed(dp).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (dp ? dec + f : '');
    };
    const forms = {
      amount: n('.', ','),
      amount_no_decimals: n('.', ',', 0),
      amount_with_comma_separator: n(',', '.'),
      amount_no_decimals_with_comma_separator: n(',', '.', 0),
      amount_with_space_separator: n(',', '\u00a0'),
      amount_no_decimals_with_space_separator: n(',', '\u00a0', 0),
      amount_with_apostrophe_separator: n('.', "'"),
    };
    return String(fmt || '{{amount_with_comma_separator}} €').replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => forms[k] ?? m);
  };

  /* ------------------------------------------------------------ galleria */
  // Värit: kuvilla on data-color (tyhjä = ei liity väriin). Näytetään valitun värin kuvat ja väriin liittämättömät.
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('.gallery__viewport', this);
      if (!this.viewport) return;
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('.gallery__thumb', this);
      this.counter = $('[data-gallery-index]', this);
      this.total = $('[data-gallery-total]', this);
      this.lightbox = $('[data-lightbox]', this);

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(t.dataset.thumb)));
      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const vis = this.visible();
          const i = this.index() + (e.key === 'ArrowRight' ? 1 : -1);
          const target = vis[Math.max(0, Math.min(vis.length - 1, i))];
          if (target) this.goTo(target.dataset.mediaId);
        }
      });
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting && en.intersectionRatio > 0.6) this.setActive(en.target.dataset.mediaId);
          });
        },
        { root: this.viewport, threshold: [0.6] },
      );
      this.slides.forEach((s) => io.observe(s));

      // Suurennus
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (z && this.lightbox) this.openLightbox(z.dataset.zoom, z);
        if (e.target.closest('[data-lightbox-close]')) this.lightbox.close();
      });
      this.lightbox?.addEventListener('click', (e) => {
        if (e.target === this.lightbox) this.lightbox.close();
      });
      this.lightbox?.addEventListener('close', () => this.zoomOpener?.focus());

      // Valitun variantin kuva näkyviin myös silloin, kun se ei ole värin ensimmäinen kuva
      const active = this.slides.find((s) => s.classList.contains('is-active'));
      if (active && active !== this.visible()[0]) this.goTo(active.dataset.mediaId, 'auto');
      this.updateCounter();
    }
    visible() {
      return this.slides.filter((s) => !s.classList.contains('is-other'));
    }
    index() {
      return Math.max(0, this.visible().findIndex((s) => s.classList.contains('is-active')));
    }
    updateCounter() {
      if (this.counter) this.counter.textContent = this.index() + 1;
      // Pikkukuvien nimet näkyvien kuvien mukaan ("Näytä kuva 2"), väri perään
      const tpl = $('.gallery__thumbs', this)?.dataset.label;
      if (tpl) {
        const ids = this.visible().map((s) => s.dataset.mediaId);
        this.thumbs.forEach((t) => {
          const i = ids.indexOf(t.dataset.thumb);
          if (i === -1) return;
          const color = t.closest('[data-color]')?.dataset.color;
          t.setAttribute('aria-label', tpl.replace('§', i + 1) + (color ? ` – ${color}` : ''));
        });
      }
      if (this.total) {
        const n = this.visible().length;
        this.total.textContent = n;
        this.total.closest('.gallery__counter').hidden = n < 2;
      }
    }
    setActive(id) {
      this.slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === String(id)));
      this.thumbs.forEach((t) => (t.dataset.thumb === String(id) ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      this.updateCounter();
    }
    goTo(id, behavior) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(id));
      if (!slide || slide.classList.contains('is-other')) return;
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: behavior || (smooth ? 'smooth' : 'auto') });
      this.setActive(id);
    }
    // Näytä vain valitun värin kuvat (ja väriin liittämättömät). Palauttaa false, jos värillä ei ole omia kuvia.
    showColor(color) {
      const own = this.slides.some((s) => s.dataset.color === color);
      $$('[data-color]', this).forEach((el) => {
        const c = el.dataset.color;
        el.classList.toggle('is-other', own && c !== '' && c !== color);
      });
      const vis = this.visible();
      if (vis.length && !vis.some((s) => s.classList.contains('is-active'))) this.setActive(vis[0].dataset.mediaId);
      this.updateCounter();
      return own;
    }
    openLightbox(id, opener) {
      this.zoomOpener = opener;
      this.lightbox.showModal();
      const item = $(`[data-lightbox-id="${id}"]`, this.lightbox);
      item?.scrollIntoView({ block: 'start' });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------ variaatiot */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = JSON.parse($('[data-variants]', this).textContent);
      this.variants = data.variants;
      this.notes = data.notes || {};
      // Alennuksen vertailuhinta senteissä varianteittain (snippets/sale-compare.liquid), null = ei alennusta
      this.sale = data.sale || {};
      this.root = this.closest('product-info');
      this.form = this.closest('form');
      this.idInput = $('[data-variant-id]', this.form);
      this.colorIndex = Number(this.dataset.colorIndex ?? -1);
      this.addEventListener('change', (e) => this.update(e.target));
      this.markAvailability();
      // Kuvavalinnoissa nimi näkyy otsikossa, kun osoitin tai fokus on valinnan päällä
      $$('.variants__chip--swatch', this).forEach((label) => {
        const input = $(`#${CSS.escape(label.htmlFor)}`, this);
        const out = $(`[data-selected-label="${input.dataset.optionIndex}"]`, this);
        const show = () => (out.textContent = input.value);
        const reset = () => (out.textContent = $(`input[name="${input.name}"]:checked`, this)?.value || '');
        label.addEventListener('mouseenter', show);
        label.addEventListener('mouseleave', reset);
        input.addEventListener('focus', show);
        input.addEventListener('blur', reset);
      });
    }
    selected() {
      return $$('fieldset', this).map((fs) => $('input:checked', fs)?.value);
    }
    current() {
      const sel = this.selected();
      return this.variants.find((v) => v.options.every((o, i) => o === sel[i]));
    }
    markAvailability() {
      const sel = this.selected();
      $$('fieldset', this).forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const combo = [...sel];
          combo[i] = input.value;
          const match = this.variants.find((v) => v.options.every((o, j) => o === combo[j]));
          const label = $(`label[for="${input.id}"]`, fs);
          label?.classList.toggle('is-unavailable', !match || !match.available);
          label?.classList.toggle('is-missing', !match);
        });
      });
    }
    // Valittua yhdistelmää ei ole (esim. väri, jota ei ole valitussa koossa): siirrytään lähimpään olemassa olevaan
    // varianttiin, jossa juuri muutettu valinta säilyy – mieluiten saatavilla olevaan ja muut valinnat ennallaan
    snap(index) {
      const sel = this.selected();
      const score = (v) => (v.available ? 100 : 0) + v.options.reduce((s, o, j) => s + (j !== index && o === sel[j] ? 10 - j : 0), 0);
      const best = this.variants.filter((v) => v.options[index] === sel[index]).sort((a, b) => score(b) - score(a))[0];
      if (!best) return;
      $$('fieldset', this).forEach((fs, j) => {
        const input = $$('input', fs).find((x) => x.value === best.options[j]);
        if (input) input.checked = true;
      });
    }
    async update(changed) {
      if (!this.current() && changed?.dataset.optionIndex != null) this.snap(Number(changed.dataset.optionIndex));
      const sel = this.selected();
      sel.forEach((v, i) => {
        const el = $(`[data-selected-label="${i}"]`, this);
        if (el) el.textContent = v;
      });
      this.markAvailability();
      const gallery = document.querySelector('product-gallery');
      if (this.colorIndex >= 0 && sel[this.colorIndex]) gallery?.showColor(sel[this.colorIndex]);
      const variant = this.current();
      const addBtn = $('[data-add-button]', this.form);
      const addLabel = $('[data-add-label]', this.form);
      const buybarBtn = $('[data-buybar-add]');
      const strings = window.theme.strings;

      if (!variant) {
        this.idInput.value = '';
        this.idInput.disabled = true;
        addBtn.disabled = true;
        addLabel.textContent = strings.unavailable;
        if (buybarBtn) {
          buybarBtn.disabled = true;
          buybarBtn.textContent = strings.unavailable;
        }
        $('[data-availability]', this.root).innerHTML = `<span class="availability availability--out">${strings.unavailable}</span>`;
        return;
      }
      this.idInput.value = variant.id;
      this.idInput.disabled = !variant.available;
      addBtn.disabled = !variant.available;
      addLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
      if (buybarBtn) {
        buybarBtn.disabled = !variant.available;
        buybarBtn.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
      if (variant.featured_media) gallery?.goTo(variant.featured_media.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Hinta, alennus ja saatavuus Shopifyn renderöiminä
      const section = this.root.dataset.section;
      const reqId = (this.reqId = (this.reqId || 0) + 1);
      this.fallbackPrice(variant);
      try {
        const res = await fetch(`${this.root.dataset.url}?variant=${variant.id}&section_id=${section}`);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (reqId !== this.reqId) return;
        for (const sel of ['[data-price-wrap]', '[data-availability]']) {
          const fresh = $(sel, doc);
          const old = $(sel, this.root);
          if (fresh && old) old.innerHTML = fresh.innerHTML;
        }
        const bp = $('[data-buybar-price]', doc);
        const bpOld = $('[data-buybar-price]');
        if (bp && bpOld) bpOld.textContent = bp.textContent;
      } catch {
        /* variantin hinta näkyy jo (fallbackPrice); lisäys koriin käyttää oikeaa varianttia */
      }
    }
    // Hinta heti variantin tiedoista; Shopifyn renderöimä osio korvaa sen, kun vastaus tulee
    fallbackPrice(variant) {
      const fmt = window.theme.moneyFormat;
      const wrap = $('[data-price-wrap]', this.root);
      const compare = this.sale[variant.id];
      const onSale = compare > variant.price;
      const cur = wrap && $('.price__current', wrap);
      if (cur) cur.textContent = formatMoney(variant.price, fmt);
      let cmp = wrap && $('.price__compare', wrap);
      if (wrap && onSale && !cmp) {
        cmp = document.createElement('s');
        cmp.className = 'price__compare';
        $('[data-price]', wrap)?.append(cmp);
      }
      if (cmp) {
        cmp.hidden = !onSale;
        cmp.textContent = onSale ? formatMoney(compare, fmt) : '';
      }
      // Alimman hinnan teksti on variantin oma; Shopifyn renderöimä osio palauttaa sen
      const lowest = wrap && $('.price__lowest', wrap);
      if (lowest) lowest.hidden = true;
      const badge = $('[data-discount]', this.root);
      if (badge) {
        badge.hidden = !onSale;
        if (onSale) badge.textContent = window.theme.strings.discount.replace('§', Math.round(((compare - variant.price) * 100) / compare));
      }
      const bp = $('[data-buybar-price]');
      if (bp) bp.textContent = formatMoney(variant.price, fmt);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------ lahjakortin saaja */
  // Kentät ovat käytössä vain, kun ”Lähetä saajalle” on valittu (muuten Shopify hylkäisi puutteelliset tiedot).
  class GiftRecipient extends HTMLElement {
    connectedCallback() {
      this.toggle = $('[data-recipient-toggle]', this);
      this.fields = $('[data-recipient-fields]', this);
      this.toggle.addEventListener('change', () => this.update());
      // Lomakkeen oma kuuntelija ehtii ennen teeman document-tason lisäystä ostoskoriin.
      this.toggle.form?.addEventListener('submit', (e) => {
        if (!this.toggle.checked) return;
        const invalid = $$('input, textarea', this.fields).find((el) => !el.checkValidity());
        if (!invalid) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        invalid.reportValidity();
      });
      this.update();
    }
    update() {
      const on = this.toggle.checked;
      this.fields.hidden = !on;
      for (const el of $$('input, textarea', this.fields)) el.disabled = !on;
      const offset = $('[data-recipient-offset]', this);
      if (offset) offset.value = String(new Date().getTimezoneOffset());
    }
  }
  customElements.define('gift-recipient', GiftRecipient);

  /* ------------------------------------------------------------ mobiilin ostopalkki */
  const bar = $('[data-buybar]');
  const add = $('[data-add-button]');
  if (bar && add && 'IntersectionObserver' in window) {
    bar.hidden = false;
    // Tila lasketaan uudelleen aina, kun painike tai jokin sen alapuolinen osio tulee näkyviin/poistuu:
    // nopeakaan vieritys ei hyppää tilamuutoksen yli.
    const update = () => bar.classList.toggle('is-visible', add.getBoundingClientRect().bottom < 0);
    const io = new IntersectionObserver(update, { threshold: [0, 0.01, 1] });
    [add, ...document.querySelectorAll('.product-details, product-recommendations, .footer')].forEach((el) => io.observe(el));
  }
})();
